/**
 * The natural-language way in: describe a plan, get the form filled in.
 *
 * This component holds no model and no transport — the host injects `propose`, so the package
 * stays free of API keys and endpoints, and the AI stays strictly a *proposer*: whatever comes
 * back is loaded into the same draft a user could have typed, then validated and rendered by
 * the deterministic layer (ADR 0017 §5).
 */

import { useState } from 'react';
import type { EventDraft } from '@project/event-format';

import { EN_MESSAGES, type EventCreatorMessages } from './messages';

/** What a proposer returns — structurally the `ProposedEvent` of `@project/inference`. */
export interface DraftProposal {
  readonly draft: EventDraft | null;
  /** Values the model supplied that the text didn't state; always shown to the user. */
  readonly assumptions?: readonly string[];
  /** Present when the model needs more before an event exists. */
  readonly question?: string;
}

export type ProposeDraft = (text: string) => Promise<DraftProposal>;

export function DescribeEvent({
  propose,
  onDraft,
  messages = EN_MESSAGES,
}: {
  propose: ProposeDraft;
  onDraft: (draft: EventDraft) => void;
  /** This box's own words. Already merged over English by the form that renders it. */
  messages?: EventCreatorMessages;
}): React.JSX.Element {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [assumptions, setAssumptions] = useState<readonly string[]>([]);
  const [question, setQuestion] = useState<string | null>(null);

  const run = async (): Promise<void> => {
    if (text.trim() === '' || busy) return;
    setBusy(true);
    setQuestion(null);
    try {
      const proposal = await propose(text.trim());
      setAssumptions(proposal.assumptions ?? []);
      setQuestion(proposal.question ?? null);
      if (proposal.draft !== null) onDraft(proposal.draft);
    } catch {
      setQuestion(messages.describeFailed);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ec-panel ec-describe">
      <h2>{messages.describeHeading}</h2>
      <textarea
        id="ec-describe"
        aria-label={messages.describeLabel}
        value={text}
        placeholder={messages.describePlaceholder}
        onChange={(event) => setText(event.target.value)}
      />
      <div className="ec-actions">
        <button type="button" className="ec-button ec-secondary" onClick={run} disabled={busy}>
          {busy ? messages.describeBusy : messages.describeAction}
        </button>
      </div>
      {question !== null && (
        <p className="ec-problem" role="status">
          {question}
        </p>
      )}
      {assumptions.length > 0 && (
        <ul className="ec-assumptions">
          {assumptions.map((assumption) => (
            <li key={assumption}>{assumption}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
